import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import cartService from "../../../../services/cart.service";
import { getShopifyCartUrl } from "../../../../utils/shopify-cart-url";
import { mapStorefrontCartJsLineToCheckoutItem } from "../../../../../shared/checkout-tracking.js";

const Spinner = ({ className = "h-8 w-8" }) => (
  <div
    className={`inline-block animate-spin rounded-full border-2 border-neutral-300 border-t-neutral-800 ${className}`}
    role="status"
    aria-label="Loading"
  />
);

const IconBtn = ({ children, onClick, className = "", ...rest }) => (
  <button
    type="button"
    onClick={onClick}
    className={`inline-flex items-center justify-center rounded p-1 text-neutral-700 hover:bg-neutral-100 ${className}`}
    {...rest}
  >
    {children}
  </button>
);

const CloseSvg = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
    <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
  </svg>
);

const DeleteSvg = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
    <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" />
  </svg>
);

const MinusSvg = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
    <path d="M19 13H5v-2h14v2z" />
  </svg>
);

const PlusSvg = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
    <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" />
  </svg>
);

/** Original cart drawer modal — unchanged behavior for existing templates / flows. */
const LegacyCartDrawerModal = ({ open, onClose }) => {
  const [cartData, setCartData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingLines, setLoadingLines] = useState([]);

  const fetchCart = async () => {
    setLoading(true);
    try {
      const res = await fetch(getShopifyCartUrl("cart.js"));
      const data = await res.json();
      setCartData(data);
    } catch (err) {
      console.error("Error fetching cart:", err);
    } finally {
      setLoading(false);
    }
  };

  const updateQuantity = async (line, quantity) => {
    setLoadingLines((prev) => [...prev, line]);
    try {
      await fetch(getShopifyCartUrl("cart/change.js"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ line, quantity }),
      });
      // Re-fetch cart.js — change.js can return wrong variant images.
      await fetchCart();
    } catch (err) {
      console.error("Error updating quantity:", err);
    } finally {
      setLoadingLines((prev) => prev.filter((l) => l !== line));
    }
  };

  useEffect(() => {
    if (open) fetchCart();
  }, [open]);

  const formatPrice = (priceInCents) => `Rs. ${(priceInCents / 100).toFixed(2)}`;

  const handleCheckout = async () => {
    if (!cartData?.items?.length) return;

    const items = cartData.items
      .map((item) => mapStorefrontCartJsLineToCheckoutItem(item))
      .filter(Boolean);

    try {
      const result = await cartService.getCheckoutUrl(items);
      if (result?.data?.data?.cart?.checkoutUrl) {
        window.location.href = result?.data?.data?.cart?.checkoutUrl;
      } else {
        console.error("Checkout URL not received.");
      }
    } catch (err) {
      console.error("Checkout failed:", err);
    }
  };

  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0 z-[1300] flex justify-end" role="presentation">
      <button
        type="button"
        className="absolute inset-0 bg-black/40"
        aria-label="Close cart overlay"
        onClick={onClose}
      />
      <aside
        className="relative flex h-full w-full max-w-full flex-col bg-white font-[Poppins,sans-serif] shadow-2xl sm:w-[400px]"
        style={{ animation: "cartDrawerFadeIn 0.5s ease forwards" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4">
          <h2 className="text-lg font-semibold">Cart • {cartData?.items?.length || 0}</h2>
          <IconBtn onClick={onClose} aria-label="Close cart">
            <CloseSvg />
          </IconBtn>
        </div>
        <hr className="border-neutral-200" />

        <div className="min-h-0 flex-1 overflow-y-auto px-[11px] py-4">
          {loading ? (
            <div className="flex h-full items-center justify-center pt-8">
              <Spinner />
            </div>
          ) : cartData?.items?.length === 0 || cartData === null ? (
            <p className="flex h-full items-center justify-center text-lg">No items in the cart.</p>
          ) : (
            cartData.items.map((item, index) => {
              const line = index + 1;
              const totalPrice = item.final_line_price;
              const originalTotal = item.original_line_price;
              const savings = originalTotal - totalPrice;

              return (
                <div key={item.id}>
                  <div className="relative pb-2">
                    <div className="flex gap-2">
                      <img
                        src={item.image}
                        alt={item.title}
                        className="h-20 w-20 shrink-0 rounded-lg border border-neutral-200 object-contain"
                      />

                      <div className="flex w-full flex-col gap-1">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-[13px] font-semibold">{item.product_title}</p>
                            <p className="text-[13px] text-neutral-600">{item.variant_title}</p>
                          </div>
                          <IconButtonSmall onClick={() => updateQuantity(line, 0)} aria-label="Remove line">
                            <DeleteSvg />
                          </IconButtonSmall>
                        </div>

                        <div className="flex items-center justify-between">
                          <div className="inline-flex h-8 items-center rounded border border-neutral-300">
                            {loadingLines.includes(line) ? (
                              <div className="flex items-center gap-1 px-2">
                                <Spinner className="h-4 w-4" />
                              </div>
                            ) : (
                              <>
                                <IconButtonSmall
                                  onClick={() => updateQuantity(line, item.quantity - 1)}
                                  aria-label="Decrease quantity"
                                >
                                  <MinusSvg />
                                </IconButtonSmall>
                                <span className="px-1 text-[13px]">{item.quantity}</span>
                                <IconButtonSmall
                                  onClick={() => updateQuantity(line, item.quantity + 1)}
                                  aria-label="Increase quantity"
                                >
                                  <PlusSvg />
                                </IconButtonSmall>
                              </>
                            )}
                          </div>

                          <div className="min-w-[80px] text-right">
                            <div className="flex items-baseline justify-end gap-1">
                              <span className="pt-1 text-[13px] text-neutral-500 line-through">
                                {formatPrice(originalTotal)}
                              </span>
                              <span className="text-[13px] font-bold">{formatPrice(totalPrice)}</span>
                            </div>
                            {item.line_level_discount_allocations?.length > 0 && (
                              <p className="text-[13px] text-green-700">(Save {formatPrice(savings)})</p>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  {index !== cartData.items.length - 1 && <hr className="mb-2 border-neutral-200" />}
                </div>
              );
            })
          )}
        </div>

        {!loading && cartData?.items?.length > 0 && (
          <div className="border-t border-neutral-200 p-2">
            <div className="mb-2">
              <div className="mb-0.5 flex items-center justify-between">
                <span className="text-sm font-medium">Subtotal</span>
                <span className="text-sm font-semibold">{formatPrice(cartData?.total_price || 0)}</span>
              </div>
              <p className="text-xs text-neutral-600">Taxes and shipping calculated at checkout</p>
            </div>
            <button
              type="button"
              className="w-full rounded bg-black py-2.5 text-sm font-medium text-white hover:bg-neutral-900"
              onClick={() => handleCheckout()}
            >
              Checkout
            </button>
          </div>
        )}
      </aside>
    </div>,
    document.body
  );
};

function IconButtonSmall({ children, ...rest }) {
  return (
    <button
      type="button"
      className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded text-neutral-700 hover:bg-neutral-100"
      {...rest}
    >
      {children}
    </button>
  );
}

export default LegacyCartDrawerModal;
